import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../store/hooks";
import { createNotification } from "../../store/slices/notificationSlice";
import AuthLayout from "./AuthLayout";
import OtpVerifyIcon from "../../assets/auth/forgotPasword.svg";
import BackArrowIcon from "../../assets/auth/back-arrow.svg";
import { db } from "../../config/firebase";
import { getFunctions, httpsCallable } from "firebase/functions";

import { collection, query, where, getDocs } from "firebase/firestore";

const ForgotPassword = () => {
  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const [userId, setUserId] = useState<string | null>(null);
  const [touched, setTouched] = useState({
    email: false,
  });

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const validateEmail = useCallback((email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }, []);

  // Function to find user by email in Firestore
  const fetchUserByEmail = useCallback(async (emailToCheck: string) => {
    if (!emailToCheck || !validateEmail(emailToCheck)) return;

    try {
      const usersRef = collection(db, "users");
      const q = query(usersRef, where("email", "==", emailToCheck));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // Get the first matching document
        const userDoc = querySnapshot.docs[0];
        setUserId(userDoc.id); // The document ID is the user's UID
        console.log("Found user UID:", userDoc.id);
      } else {
        console.log("No user found with this email");
        setUserId(null);
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      setUserId(null);
    }
  }, [validateEmail]);

  useEffect(() => {
    if (email) {
      fetchUserByEmail(email);
    }
  }, [email, fetchUserByEmail]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    // Only show error if field has been touched
    if (touched.email) {
      if (!newEmail.trim()) {
        setError("Email is required");
      } else if (!validateEmail(newEmail)) {
        setError("Please enter a valid email address");
      } else {
        setError("");
      }
    }
  };

  const handleBlur = () => {
    setTouched({ email: true });
    if (!email.trim()) {
      setError("Email is required");
    } else if (!validateEmail(email)) {
      setError("Please enter a valid email address");
    } else {
      setError("");
    }
  };

  const handleFocus = () => {
    // Clear error when user starts typing again
    if (error) {
      setError("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    // Check if the email exists in our database first
    if (!userId) {
      setError(
        "Email not found. Please register first before attempting to reset password."
      );
      return;
    }

    setLoading(true);
    setError("");

    try {
      const functions = getFunctions();
      const sendPasswordReset = httpsCallable(functions, "sendPasswordResetEmail");
      
      await sendPasswordReset({ email });

      // Create notification about password reset
      if (userId) {
        dispatch(
          createNotification({
            userId: userId,
            userEmail: email,
            message:
              "You have successfully been sent a link to reset your password",
            trigger: "password_reset",
            triggeredBy: userId,
          })
        );
      }

      navigate("/email-send", {
        state: { email: email },
      });
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to send password reset email";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className=" flex items-center justify-center bg-white">
        <div className="flex-1 flex flex-col justify-center p-8  items-center mt-12">
          <div className="">
            <div className="text-center">
              <img
                src={OtpVerifyIcon}
                alt="Help Illustration"
                className="mx-auto"
              />

              <div className="flex justify-center items-center gap-5 mt-2">
                <button onClick={() => navigate(-1)}>
                  <img
                    src={BackArrowIcon}
                    alt="back arrow"
                    className="h-[48px] w-[48px] text-[#919191]"
                  />
                </button>
                <h2 className="text-title text-[48px] nunito-extrabold text-[#0A0A0A] w-[90%]">
                  Forgot Password
                </h2>
              </div>

              <p className="mt-2 text-[24px] items-start text-[#696969] text-center nunito-regular max-w-[489px] mx-auto">
                No worries! Enter your email address below, and we will send you
                a link to reset your password
              </p>
            </div>
            <form className=" mt-6" onSubmit={handleSubmit}>
              <div className="flex justify-center gap-1  mt-10 flex-col px-20">
                <label
                  htmlFor="email"
                  className="block text-[14px] nunito-semibold text-[#1D1D1D]"
                >
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleBlur}
                  onFocus={handleFocus}
                  disabled={loading}
                  className={`w-[320px] h-[40px] px-4 py-2 border text-[16px] rounded-[6px] text-[#919191] nunito-medium focus:outline-none focus:ring-2 
                      ${
                        touched.email && error
                          ? "border-[#F55A51] text-[#F55A51] focus:ring-[#F55A51]"
                          : "border-gray-300 focus:ring-orange-500"
                      }`}
                  placeholder="Enter your email address"
                />
                {touched.email && error && (
                  <div className="text-[#D5332A] text-[10px] nunito-medium mt-1">
                    {error}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={
                    loading || (touched.email && (!email || !!error))
                  }
                  className={`mt-7 w-[320px] h-[40px] flex items-center justify-center px-4 py-2 text-white text-[16px] nunito-bold rounded-[8px] 
                      ${
                        loading || (touched.email && (!email || !!error))
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-[#F15D18] hover:bg-primary"
                      } 
                      focus:ring-2 focus:ring-orange-500`}
                >
                  {loading ? "Sending..." : "Submit"}
                </button>
              </div>
              <div className="flex flex-col items-center mt-6 ml-4"></div>
            </form>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default ForgotPassword;
